import BookingAboutToExpireDialog from '@/components/purchase/checkout/expiration-timer/BookingAboutToExpireDialog';
import BookingExpiredDialog from '@/components/purchase/checkout/expiration-timer/BookingExpiredDialog';
import { resetPurchase } from '@/features/purchase/purchaseActions';
import type { CountdownTime } from '@/hooks/useInProgressBooking';
import { useInProgressBooking } from '@/hooks/useInProgressBooking';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { persistor } from '@/store';
import { useDispatch } from '@/store/utils';
import { formatCountdown } from '@/utils/dateTime';
import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

interface BookingExpiryContextValue {
  countdown: CountdownTime | null;
  isExpiring: boolean;
  isExpired: boolean;
  dismissExpiringWarning: () => void;
}

const BookingExpiryContext = createContext<BookingExpiryContextValue | null>(
  null
);

interface BookingExpiryProviderProps {
  children: React.ReactNode;
}

export function BookingExpiryProvider({
  children,
}: BookingExpiryProviderProps): React.ReactElement {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setCurrentSearchFormValues } = useSearchWidget();
  const { bookingId, booking, isValidating, countdown } =
    useInProgressBooking();
  const [expiringDismissed, setExpiringDismissed] = useState(false);

  // Calculate expiry flags
  const isExpiring =
    countdown !== null && countdown.minutes < 5 && countdown.minutes >= 0;
  const isExpired =
    countdown !== null && countdown.minutes === 0 && countdown.seconds === 0;

  // Safety guard: only show modals after validation completes AND booking exists
  const canShowModals = !isValidating && !!bookingId;

  // Only show expired modal if countdown is initialized (not null) and expired
  // Null countdown means no booking data yet, so we won't show the modal
  const showExpiredDialog = canShowModals && isExpired;

  // Expiring dialog can show on hydration (legitimate warning for active bookings)
  const showExpiringDialog =
    canShowModals && isExpiring && !expiringDismissed && !isExpired;

  // Reset dismiss state when booking changes
  const previousBookingIdRef = useRef(bookingId);
  if (bookingId !== previousBookingIdRef.current) {
    previousBookingIdRef.current = bookingId;
    setExpiringDismissed(false);
  }

  const dismissExpiringWarning = useCallback(() => {
    setExpiringDismissed(true);
  }, []);

  const handleBookingExpired = useCallback(() => {
    dispatch(resetPurchase());
    setCurrentSearchFormValues(undefined);
    persistor.purge();
    navigate('/purchase');
  }, [dispatch, navigate, setCurrentSearchFormValues]);

  const formattedCountdown = formatCountdown(countdown);

  const contextValue = useMemo<BookingExpiryContextValue>(
    () => ({
      countdown,
      isExpiring,
      isExpired,
      dismissExpiringWarning,
    }),
    [countdown, isExpiring, isExpired, dismissExpiringWarning]
  );

  return (
    <BookingExpiryContext.Provider value={contextValue}>
      {children}

      {/* Global expiry modals */}
      <BookingAboutToExpireDialog
        open={showExpiringDialog}
        timeTillExpiry={formattedCountdown}
        setOpen={(open) => {
          if (!open) dismissExpiringWarning();
        }}
        onCancel={dismissExpiringWarning}
      />
      <BookingExpiredDialog
        open={showExpiredDialog}
        setOpen={() => {}}
        onCancel={handleBookingExpired}
      />
    </BookingExpiryContext.Provider>
  );
}

export function useBookingExpiry(): BookingExpiryContextValue {
  const context = useContext(BookingExpiryContext);
  if (!context) {
    throw new Error(
      'useBookingExpiry must be used within BookingExpiryProvider'
    );
  }
  return context;
}
